import * as React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';
import i18n from '../i18n';
import store from '../redux-store/store';
import AsyncBatchModal from '../components/common/async-batch-modal';

/**
 * Service for managing the async batch modal globally
 * Provides a singleton interface for showing/hiding the modal across different pages
 */
class AsyncBatchModalService {
    private static instance: AsyncBatchModalService;

    private containerElement: HTMLElement | null = null;

    private isModalVisible: boolean = false;

    private modalProps: any = null;

    private constructor() {
        // Private constructor to enforce singleton pattern
    }

    public static getInstance(): AsyncBatchModalService {
        if (!AsyncBatchModalService.instance) {
            AsyncBatchModalService.instance = new AsyncBatchModalService();
        }
        return AsyncBatchModalService.instance;
    }

    private initialize() {
        if (!this.containerElement) {
            this.containerElement = document.createElement('div');
            this.containerElement.id = 'async-batch-modal-container';
            document.body.appendChild(this.containerElement);
        }
    }

    private cleanup() {
        if (this.containerElement && this.containerElement.children.length > 0) {
            this.isModalVisible = false;
            this.modalProps = null;
            ReactDOM.unmountComponentAtNode(this.containerElement);
            document.body.removeChild(this.containerElement);
            this.containerElement = null;
        }
    }

    public showModal(props: {
        heading: string;
        data: any[];
        apiCallFn: (requestData: any) => Promise<any>;
        getRequestBody: (batchData: any[]) => any;
        failureArrayKeysToShow: string[];
        isSingleApi?: boolean;
        onAllBatchesComplete?: () => Promise<any>;
        dataHeaders?: any;
        concurrencyLimit?: number;
        onClose?: () => void;
    }): void {
        try {
            this.initialize();
            this.isModalVisible = true;
            this.modalProps = props;
            if (!this.containerElement || !this.containerElement.children.length) {
                // Wrap with Redux Provider and I18nextProvider
                ReactDOM.render(
                    <Provider store={store}>
                        <I18nextProvider i18n={i18n}>
                            <AsyncBatchModal
                                isVisible="true"
                                heading={props.heading}
                                apiCallFn={props.apiCallFn}
                                data={props.data}
                                getRequestBody={props.getRequestBody}
                                onClose={() => {
                                    this.cleanup();
                                    if (props.onClose) {
                                        props.onClose();
                                    }
                                }}
                                failureArrayKeysToShow={props.failureArrayKeysToShow}
                                isSingleApi={props.isSingleApi}
                                onAllBatchesComplete={props.onAllBatchesComplete}
                                dataHeaders={props.dataHeaders}
                                concurrencyLimit={props.concurrencyLimit}
                            />
                        </I18nextProvider>
                    </Provider>,
                    this.containerElement,
                );
            }
        } catch (error) {
            console.error('Error showing async batch modal:', error);
            this.cleanup();
        }
    }

    public hideModal(): void {
        this.cleanup();
    }

    public isVisible(): boolean {
        return this.isModalVisible;
    }
}

export default AsyncBatchModalService;
